import { useEffect } from 'react';
import { LocaleLink as Link } from '../i18n/locale-link';
import { useT } from '../i18n/locale-context';
import { plansPathWithContext } from '../lib/pending-checkout';
import { track } from '../lib/funnel';
import { paywallOncePerLot, usePaywallBlock } from '../lib/paywall-view';
import { ArrowRight, TrophyIcon } from './icons';
import { LockGlyph } from './locked-chip';
import { BlurredValue } from './blurred-value';
import { withCurrency } from '../lib/format';
import { LOCKED_PHONE_PLACEHOLDER, LOCKED_WINNER_PLACEHOLDER } from '../lib/locked-placeholders';
import { outcomeLockText, type OutcomeLockFlags } from '../lib/outcome-lock-text';

/**
 * The outcome wall (owner, 2026-10-07): a closed lot HAS a winner, and who it
 * is and for how much is Pro. Rendered only when the API said so —
 * `outcomeLocked: true`, which it sends to a guest, a free account and the
 * anonymous prerender in place of `winnerName`/`winnerInn`/`finalPriceUzs`.
 * Nothing here re-derives entitlement: there is no winner in the payload to
 * hide, so a web build that lands before (or after) the API cannot leak one.
 *
 * One action for everyone — «Оформить Pro» → /plans with this lot in
 * `?tender=`, so checkout returns them here — counted as
 * `upgrade_cta_clicked{source:'outcomeWall'}`. A guest used to be sent to
 * /register instead (2026-10-07 review): ad traffic lands on these pages and
 * the owner keeps guest checkout, so a detour through registration only cost
 * buyers.
 *
 * `.tender-paid`: the JSON-LD paywall declaration targets it, so the page's
 * structured data says honestly that this part is behind the wall.
 *
 * ## The look (Igor, 10.10)
 *
 * «Замок должен быть ТАКИМ ЖЕ, как у остальных закрытых блоков». It was a
 * small chip with a long sentence squeezed into it; it is now the locked
 * teaser every other gated card on the page wears (`AnalyticsTeaser`): an icon
 * header with the 🔒 on the right, the REAL outcome card's fields with blurred
 * values under it, then ONE line and one «Оформить Pro». The decision date is
 * public and stays readable.
 *
 * The blurred values are fixed placeholders — «ООО «Хххххх»» (an S.L. on
 * Spain, `lib/locked-placeholders.ts`), all-8 digits —
 * exactly like `BlurredValue`'s rule 1: there is no winner in the payload to
 * blur, and un-blurring them in devtools yields nonsense.
 */
/** Placeholder text behind the blur. Never real data — see the note above. */
const PLACEHOLDER_NAME = LOCKED_WINNER_PLACEHOLDER;
const PLACEHOLDER_PRICE = '888 888 888';
const PLACEHOLDER_PHONE = LOCKED_PHONE_PLACEHOLDER;

export function OutcomeWall({
  tenderId,
  awardedAt,
  flags = {},
}: {
  tenderId?: string;
  /** Already formatted — the decision date stays public. */
  awardedAt?: string | null;
  /** What exists behind the wall — picks the sentence (`outcomeLockText`) and
   *  which blurred fields are drawn. */
  flags?: OutcomeLockFlags;
}) {
  const t = useT();
  // Same reading as `outcomeLockText`: a missing flag (an API one deploy
  // behind) counts as «present», never as an under-sell.
  const hasPrice = flags.outcomeHasPrice !== false;
  const hasContact = flags.outcomeHasContact !== false;

  // The impression, under the shared wall vocabulary (PAYWALL_WALLS on the
  // API), with the lot it sat on — see the `tenderId` note on AnalyticsTeaser.
  // Spain (`analytics.paywallOncePerLot`): reported through the lot's single wall hit.
  const block = usePaywallBlock('outcome', tenderId);
  useEffect(() => {
    if (paywallOncePerLot()) return;
    track('paywall_shown', { wall: 'outcome', ...(tenderId ? { tenderId } : {}) });
  }, [tenderId]);

  const label = 'text-[11px] font-bold uppercase tracking-[0.05em] text-ink-muted';
  const value = 'mt-0.5 block text-[14px] font-semibold text-ink';

  return (
    <section
      ref={block.ref}
      className="tender-paid overflow-hidden rounded-lg border border-edge bg-bone-surface shadow-m-sm"
      data-testid="outcome-wall"
    >
      <div className="flex items-center gap-3 px-5 pb-4 pt-5 sm:px-6">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-brand-dim text-brand">
          <TrophyIcon className="h-[18px] w-[18px]" />
        </span>
        <h2 className="min-w-0 flex-1 text-[15px] font-bold text-ink">
          {t('tender.outcome.label')}
        </h2>
        <LockGlyph className="shrink-0 text-[13px] text-ink-muted/70" />
      </div>

      <dl className="grid grid-cols-1 gap-x-4 gap-y-3 border-t border-edge-light px-5 py-4 sm:grid-cols-2 sm:px-6">
        <div className="min-w-0">
          <dt className={label}>{t('tender.outcome.winner')}</dt>
          <dd>
            <BlurredValue className={value}>{PLACEHOLDER_NAME}</BlurredValue>
          </dd>
        </div>
        {hasPrice && (
          <div className="min-w-0">
            <dt className={label}>{t('tender.outcome.finalPrice')}</dt>
            <dd>
              <BlurredValue className={value}>
                {withCurrency(PLACEHOLDER_PRICE, t('tender.budget.unit'))}
              </BlurredValue>
            </dd>
          </div>
        )}
        {hasContact && (
          <div className="min-w-0">
            <dt className={label}>{t('tender.outcome.winnerContact')}</dt>
            <dd>
              <BlurredValue className={value}>{PLACEHOLDER_PHONE}</BlurredValue>
            </dd>
          </div>
        )}
        {awardedAt && (
          <div className="min-w-0">
            <dt className={label}>{t('tender.outcome.awardedAt')}</dt>
            <dd className="mt-0.5 text-[14px] text-ink">{awardedAt}</dd>
          </div>
        )}
      </dl>

      <div className="border-t border-edge-light bg-bone px-5 py-4 sm:px-6">
        <p className="text-[14px] leading-snug text-ink" data-testid="outcome-locked-line">
          {outcomeLockText(t, flags, 'wall')}
        </p>
        <Link
          to={plansPathWithContext('paywall', tenderId)}
          onClick={() => {
            block.onTap();
            track('upgrade_cta_clicked', { source: 'outcomeWall' });
          }}
          className="mt-3 inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-md bg-brand px-[18px] py-2.5 text-center text-[14px] font-bold leading-snug text-white transition-colors hover:bg-brand-mid sm:w-auto"
        >
          {t('tender.outcome.lockedCta')}
          <ArrowRight className="h-3.5 w-3.5 shrink-0" />
        </Link>
      </div>
    </section>
  );
}
